import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  PortalResource,
  PortalResourceGetDefaultRegion,
} from 'src/resource/PortalResource/types';
import { ResourceApi } from '../../resource/Resource';
import {
  TermsOfServiceResource,
  TermsOfServiceResourceAcceptTermsOfServiceByType,
  TermsOfServiceResourceGetTermsOfServiceNextUpdateDates,
  TermsOfServiceResourceGetTermsOfServiceUpdateDates,
} from '../../resource/TermsOfServiceResource/types';
import {
  WasabiResource,
  WasabiResourceScheduleTosUpdate,
} from '../../resource/WasabiResource/types';
import { currentUserIdSelector } from '../Auth/selectors';
import { TERMS_OF_SERVICE, WASABI, PORTAL } from '../resourceNames';
import { ReduxState } from '../state';
import { TosType } from './types';

export const retrieveTosUpdateDates = createAsyncThunk<
  TermsOfServiceResourceGetTermsOfServiceUpdateDates['return'] | null,
  void,
  {
    state: ReduxState;
  }
>(
  'application/retrieveTosUpdateDates',
  async (_args, { getState, rejectWithValue }) => {
    if (!currentUserIdSelector(getState())) {
      return null;
    }

    try {
      const result = await ResourceApi.lookup<
        TermsOfServiceResource,
        TermsOfServiceResourceGetTermsOfServiceUpdateDates
      >(TERMS_OF_SERVICE, 'get_terms_of_service_update_dates', {});

      return result;
    } catch (err) {
      const responseData =
        err.response?.data || 'Error retrieving ToS Update dates';

      return rejectWithValue(responseData);
    }
  }
);

export const retrieveTosNextUpdateDates = createAsyncThunk<
  TermsOfServiceResourceGetTermsOfServiceNextUpdateDates['return'] | null,
  void,
  {
    state: ReduxState;
  }
>(
  'application/retrieveTosNextUpdateDates',
  async (_args, { getState, rejectWithValue }) => {
    if (!currentUserIdSelector(getState())) {
      return null;
    }

    try {
      const result = await ResourceApi.lookup<
        TermsOfServiceResource,
        TermsOfServiceResourceGetTermsOfServiceNextUpdateDates
      >(TERMS_OF_SERVICE, 'get_terms_of_service_next_update_dates', {});

      return result;
    } catch (err) {
      const responseData =
        err.response?.data || 'Error retrieving next ToS Update dates';

      return rejectWithValue(responseData);
    }
  }
);

export const acceptTermsOfService = createAsyncThunk<
  TermsOfServiceResourceAcceptTermsOfServiceByType['return'] | null,
  TosType,
  {
    state: ReduxState;
  }
>('application/acceptTermsOfService', async (tosType) => {
  const result = await ResourceApi.execute<
    TermsOfServiceResource,
    TermsOfServiceResourceAcceptTermsOfServiceByType
  >(TERMS_OF_SERVICE, 'accept_terms_of_service_by_type', { type: tosType });

  return result;
});

export const scheduleTosUpdate = createAsyncThunk<
  WasabiResourceScheduleTosUpdate['return'] | null,
  { datetime: number; types: TosType[] },
  {
    state: ReduxState;
  }
>(
  'application/scheduleTosUpdate',
  async ({ datetime, types }, { dispatch }) => {
    const result = await ResourceApi.execute<
      WasabiResource,
      WasabiResourceScheduleTosUpdate
    >(WASABI, 'scheduleTosUpdate', { datetime, types });

    await dispatch(retrieveTosNextUpdateDates());
    await dispatch(retrieveTosUpdateDates());

    return result;
  }
);

export const loadIpAddress = createAsyncThunk<
  string | null,
  void,
  {
    state: ReduxState;
  }
>('application/loadIpAddress', async () => {
  const response = await fetch('https://api.ipify.org');
  if (response.ok) {
    return response.text();
  } else {
    return null;
  }
});

export const loadDefaultPortalRegion = createAsyncThunk(
  'application/loadDefaultPortalRegion',
  async () => {
    return ResourceApi.lookup<PortalResource, PortalResourceGetDefaultRegion>(
      PORTAL,
      'getRegion',
      {}
    );
  }
);
